import React, { useCallback, useRef } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { FileItemRaw } from '@entities/attachment/ui';
import { TCommentType } from '@entities/comment/model/types';
import { CircularLoader, Flex, Icon, TextArea, UiButton } from '@shared/ui';
import {
    removeNoCommentsClassForPositionGuid,
    removeUnreadIndicatorForPositionGuid,
} from '@widgets/Comments/lib/polling';
import {
    useAttachments,
    useComment,
    useCommentsErrorToast,
    useCommentsFooterValidation,
    useHandleSave,
} from '@widgets/Comments/model';

import cls from './Footer.module.css';

interface ICommentsFooterProps {
    positionGuid: string;
    commentType?: TCommentType;
    className?: string;
}
export const CommentsFooter = ({ positionGuid, commentType, className }: ICommentsFooterProps) => {
    const { t } = useTranslation();
    const { attachedFiles, handleFileChange, handleCancel, clearAttachments } = useAttachments();
    const { comment, onCommentChange, clearComment } = useComment();
    const { showErrorToast } = useCommentsErrorToast(positionGuid);
    const { handleSaveComment, isPending: isSaving } = useHandleSave({
        comment,
        attachedFiles,
        positionGuid,
        commentType,
        onSuccessCb: () => {
            removeNoCommentsClassForPositionGuid(positionGuid);
            removeUnreadIndicatorForPositionGuid(positionGuid);
            clearComment();
            clearAttachments();
        },
        onErrorCb: showErrorToast,
        defaultErrorText: 'Unable to send comment! Please try again',
    });
    const {
        hasValidationError,
        handleCommentInputChange,
        clearValidationError,
        handleCommentSave,
    } = useCommentsFooterValidation({
        comment,
        attachedFilesCount: attachedFiles.length,
        onCommentChange,
        onSave: handleSaveComment,
    });

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleOpenFilePicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files?.length) {
                clearValidationError();
            }

            handleFileChange(event);
        },
        [clearValidationError, handleFileChange]
    );

    return (
        <>
            {attachedFiles.length > 0 && (
                <Flex as="ul" gap={13} className={cls.AttachmentsContainer} wrap="wrap">
                    {attachedFiles.map((item) => (
                        <FileItemRaw
                            key={item.id}
                            file={item.file}
                            isLoading={false}
                            fileItemClassName={cls.FileItemRaw}
                            onCancel={() => handleCancel(item.id)}
                            cancelButtonClassName={cls.CancelBtn}
                        />
                    ))}
                </Flex>
            )}
            <Flex
                as="footer"
                fullWidth
                className={cn(cls.FooterContainer, className)}
                align="center"
                justify="between"
                gap={16}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    className={cls.hidden}
                    onChange={handleFileInputChange}
                    multiple
                />
                <UiButton
                    variant="icon"
                    startIcon={<Icon iconName="paperClip" height={19} />}
                    className={cls.FileInputBtn}
                    title={t('Comments.Footer.AttachBtn.title')}
                    onClick={handleOpenFilePicker}
                />
                <div className={cls.FooterInputContainer}>
                    <TextArea
                        classes={cls.FooterTextArea}
                        inputName="commentInput"
                        value={comment}
                        onChange={handleCommentInputChange}
                        maxLength={2000}
                        maxRows={6}
                        autoResize
                        isDisabled={isSaving}
                        isError={hasValidationError}
                    />
                </div>
                {isSaving ? (
                    <div className={cls.SendLoaderSlot}>
                        <CircularLoader size={20} thickness={2} />
                    </div>
                ) : (
                    <UiButton
                        variant="icon"
                        startIcon={<Icon iconName="send" />}
                        className={cls.FileInputBtn}
                        title={t('Comments.Footer.SendMessageBtn.title')}
                        onClick={handleCommentSave}
                        disabled={isSaving}
                    />
                )}
            </Flex>
        </>
    );
};
